import React, { useEffect, useMemo, useState } from "react";
import { api, resolveMedia } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { Trophy, Star, Medal, WarningCircle, ShareNetwork } from "@phosphor-icons/react";
import PotgShareModal from "@/components/PotgShareModal";

const STATS = [
  { k: "pts", label: "PTS" },
  { k: "ast", label: "AST" },
  { k: "reb", label: "REB" },
  { k: "blk", label: "BLK" },
];

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" }) : "");

export const PlayerOfGame = ({ admin = false, onManage }) => {
  const { settings } = useSettings();
  const [cards, setCards] = useState([]);
  const [board, setBoard] = useState({ leaders: [], division_leaders: [], total_awards: 0 });
  const [division, setDivision] = useState("all");
  const [share, setShare] = useState(null);

  const load = () => {
    const q = division === "all" ? "" : `?division=${encodeURIComponent(division)}`;
    const sep = q ? "&" : "?";
    api.get(`/potg${q}${sep}visible_only=true`).then((r) => setCards(r.data || [])).catch(() => setCards([]));
    api.get(`/potg/leaderboard${q}`).then((r) => setBoard(r.data || {})).catch(() => {});
  };
  useEffect(load, [division]); // eslint-disable-line react-hooks/exhaustive-deps

  const divisions = useMemo(() => {
    const fromSettings = settings.divisions || [];
    const fromData = Array.from(new Set(cards.map((c) => c.division)));
    return Array.from(new Set([...fromSettings, ...fromData])).filter(Boolean);
  }, [settings.divisions, cards]);

  // Group awards into weekly sections (Week 1, Week 2, …); games without a week go last.
  const weekGroups = useMemo(() => {
    const map = new Map();
    cards.forEach((c) => {
      const w = c.week != null ? c.week : null;
      if (!map.has(w)) map.set(w, []);
      map.get(w).push(c);
    });
    const keys = Array.from(map.keys()).sort((a, b) => {
      if (a == null) return 1;
      if (b == null) return -1;
      return b - a;
    });
    return keys.map((w) => ({ week: w, cards: map.get(w) }));
  }, [cards]);

  const renderCard = (c) => (
    <div key={c.game_id} data-testid={`potg-card-${c.game_id}`} className="border border-white/10 bg-[#121212] overflow-hidden group">
      <div className="relative aspect-[4/3] bg-[#0A0A0A]">
        {c.photo_url ? <img src={resolveMedia(c.photo_url)} alt={c.player_name} className="w-full h-full object-cover" />
          : <div className="w-full h-full flex items-center justify-center"><Star size={48} weight="fill" className="text-white/10" /></div>}
        <span className="absolute top-3 left-3 bg-[#FF5722] text-black text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 flex items-center gap-1"><Trophy size={12} weight="fill" /> Player of the Game</span>
        <span className="absolute top-3 right-3 bg-black/70 text-white text-[10px] font-semibold uppercase tracking-wide px-2.5 py-1">{c.division}</span>
        <img src="/iefsc-logo.png" alt="IEFSC Basketball League" data-testid="potg-league-logo" className="absolute bottom-3 right-3 w-14 h-14 object-contain drop-shadow-[0_2px_6px_rgba(0,0,0,0.7)]" />
      </div>
      <div className="p-5">
        <div className="flex items-center justify-between gap-2">
          <p className="font-display text-2xl uppercase leading-tight">{c.number != null ? `#${c.number} ` : ""}{c.player_name}</p>
          {c.needs_review && <span title="Tie — flagged for admin review" data-testid={`potg-review-${c.game_id}`} className="text-[#FFB300]"><WarningCircle size={18} weight="fill" /></span>}
        </div>
        <p className="text-sm text-white/50 uppercase tracking-wide">{c.team_name}{c.opponent ? <span className="text-white/30"> vs {c.opponent}</span> : null}</p>
        <p className="text-xs text-white/40 mt-1">{c.week != null ? `Week ${c.week} · ` : ""}{fmtDate(c.date)}{c.final_score ? ` · ${c.final_score}` : ""}</p>
        <div className="grid grid-cols-4 gap-1 mt-4 border-t border-white/10 pt-4">
          {STATS.map((s) => (
            <div key={s.k} className="text-center">
              <div className="font-display text-xl text-white tabular-nums leading-none">{c[s.k] || 0}</div>
              <div className="text-[9px] uppercase tracking-wide text-white/40 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
        {admin && (
          <button onClick={() => onManage && onManage(c)} data-testid={`potg-manage-${c.game_id}`}
            className="mt-4 w-full text-xs uppercase font-bold tracking-wide border border-white/15 hover:border-[#FF5722] hover:text-[#FF5722] text-white/70 py-2 transition-colors">Manage Award</button>
        )}
        <button onClick={() => setShare(c)} data-testid={`potg-share-${c.game_id}`}
          className="mt-3 w-full flex items-center justify-center gap-2 text-xs uppercase font-bold tracking-wide bg-[#FF5722]/10 border border-[#FF5722]/40 hover:bg-[#FF5722] hover:text-black text-[#FF5722] py-2 transition-colors"><ShareNetwork size={15} weight="bold" /> Share Card</button>
      </div>
    </div>
  );

  return (
    <div data-testid="potg-section">
      <div className="flex gap-2 flex-wrap mb-6" data-testid="potg-filters">
        <button onClick={() => setDivision("all")} data-testid="potg-filter-all"
          className={`px-4 py-2 text-xs uppercase font-bold tracking-wide border transition-colors ${division === "all" ? "bg-[#FF5722] text-black border-[#FF5722]" : "border-white/15 text-white/60 hover:text-white"}`}>All Divisions</button>
        {divisions.map((d) => (
          <button key={d} onClick={() => setDivision(d)} data-testid={`potg-filter-${d.replace(/\s+/g, "-").toLowerCase()}`}
            className={`px-4 py-2 text-xs uppercase font-bold tracking-wide border transition-colors ${division === d ? "bg-[#FF5722] text-black border-[#FF5722]" : "border-white/15 text-white/60 hover:text-white"}`}>{d}</button>
        ))}
      </div>

      {/* Season leaders */}
      {board.leaders?.length > 0 && (
        <div className="border border-white/10 bg-[#0F0F0F] p-4 mb-8" data-testid="potg-leaders">
          <p className="text-xs uppercase tracking-[0.2em] text-white/40 font-bold mb-3 flex items-center gap-2"><Medal size={15} className="text-[#FF5722]" /> Season POTG Leaders</p>
          <div className="flex flex-wrap gap-2">
            {board.leaders.slice(0, 8).map((l, i) => (
              <div key={l.player_id} className="flex items-center gap-2 border border-white/10 bg-[#151515] px-3 py-1.5">
                <span className={`font-display text-lg tabular-nums ${i === 0 ? "text-[#FF5722]" : "text-white/50"}`}>{i + 1}</span>
                <span className="text-sm font-semibold uppercase">{l.player_name}</span>
                <span className="text-xs text-white/40">{l.team_name}</span>
                <span className="text-xs font-bold text-[#FF5722] bg-[#FF5722]/10 px-2 py-0.5">{l.awards} POTG</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {cards.length === 0 ? (
        <div className="border-2 border-dashed border-white/15 py-20 text-center" data-testid="potg-empty">
          <Trophy size={44} className="text-white/30 mx-auto mb-3" />
          <p className="text-white/50">No Player of the Game awards yet. They publish automatically when a game is finalized.</p>
        </div>
      ) : (
        <div className="space-y-10" data-testid="potg-weeks">
          {weekGroups.map((grp) => (
            <div key={grp.week ?? "none"} data-testid={`potg-week-${grp.week ?? "none"}`}>
              <div className="flex items-center gap-3 mb-4">
                <h3 className="font-display text-2xl uppercase text-white flex items-center gap-2 whitespace-nowrap">
                  <Trophy size={18} weight="fill" className="text-[#FF5722]" />
                  {grp.week != null ? `Week ${grp.week}` : "Other Games"}
                </h3>
                <span className="text-xs text-white/40 uppercase tracking-wide whitespace-nowrap">{grp.cards.length} POTG</span>
                <div className="flex-1 h-px bg-white/10" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="potg-cards">
                {grp.cards.map((c) => renderCard(c))}
              </div>
            </div>
          ))}
        </div>
      )}

      {share && <PotgShareModal card={share} onClose={() => setShare(null)} />}
    </div>
  );
};
